<template>
  <a-drawer width="92%" :visible="open" @close="onClose">
    <template slot="title">
      <span>{{ formTitle }}</span>
    </template>

    <a-spin :spinning="loading">
      <div class="master-order-detail-wrap">
        <a-steps
          v-if="form.masterOrderStatus != null"
          class="order-status-step"
          :current="masterOrderStepCurrent"
          :status="masterOrderStepStatus"
        >
          <a-step title="待支付" />
          <a-step title="接单中" />
          <a-step title="待服务" />
          <a-step title="服务中" />
          <a-step title="待评价" />
          <a-step title="已完成" />
          <a-step title="售后中" />
          <a-step title="售后完成" />
          <a-step title="已关闭" />
        </a-steps>

        <div class="order-info-card">
          <div class="order-info-card__title">师傅订单信息</div>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">订单编号：</span>
              <span class="detail-value">{{ form.orderNo || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">师傅订单状态：</span>
              <span class="detail-value">
                <a-tag :color="getMasterOrderStatusColor(form.masterOrderStatus)">
                  {{ getMasterOrderStatusText(form.masterOrderStatus) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">售后状态：</span>
              <span class="detail-value">
                <a-tag :color="getAftermarketStatusColor(form.aftermarketStatus)">
                  {{ getAftermarketStatusText(form.aftermarketStatus) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">售后次数：</span>
              <span class="detail-value">{{ form.aftermarketNum != null ? form.aftermarketNum : '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">订单单价：</span>
              <span class="detail-value">{{ formatMoney(form.orderUnitPrice) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">订单总价：</span>
              <span class="detail-value">{{ formatMoney(form.orderTotalPrice) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">报价金额：</span>
              <span class="detail-value">{{ formatMoney(form.quotationPrice) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">报价时间：</span>
              <span class="detail-value">{{ parseTime(form.quotationTime) || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">保证金金额：</span>
              <span class="detail-value">{{ formatMoney(form.bondPrice) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">保证金支付类型：</span>
              <span class="detail-value">
                <a-tag :color="getBondPayTypeColor(form.bondPayType)">
                  {{ getBondPayTypeText(form.bondPayType) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">保证金支付状态：</span>
              <span class="detail-value">
                <a-tag :color="getBondPayStatusColor(form.bondPayStatus)">
                  {{ getBondPayStatusText(form.bondPayStatus) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">开工码：</span>
              <span class="detail-value">{{ form.startCode || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">接单时间：</span>
              <span class="detail-value">{{ parseTime(form.createTime) || '-' }}</span>
            </div>
          </div>
          <div class="detail-row detail-row--block content-desc-block">
            <span class="detail-label">备注：</span>
            <span class="detail-value">{{ form.remark || '-' }}</span>
          </div>
        </div>

        <div class="order-info-card">
          <div class="order-info-card__title">关联雇主订单</div>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">招工标题：</span>
              <span class="detail-value detail-value--ellipsis">
                <a v-if="customerOrder.id" @click="openCustomerOrderDetail">{{ customerOrder.title || '查看雇主订单' }}</a>
                <span v-else>{{ customerOrder.title || '-' }}</span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">订单编号：</span>
              <span class="detail-value">{{ customerOrder.orderNo || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">订单类型：</span>
              <span class="detail-value">
                <a-tag v-if="customerOrder.type === 0" color="blue">找个人</a-tag>
                <a-tag v-else-if="customerOrder.type === 1" color="green">找公司</a-tag>
                <span v-else>-</span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">订单状态：</span>
              <span class="detail-value">
                <a-tag :color="getOrderStatusColor(customerOrder.status)">
                  {{ getOrderStatusText(customerOrder.status) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">工种：</span>
              <span class="detail-value">{{ customerOrder.categoryName || customerOrder.category || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">工作地点：</span>
              <span class="detail-value">{{ customerOrder.workLocation || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">详细地址：</span>
              <span class="detail-value detail-value--ellipsis" :title="customerOrder.address">{{ customerOrder.address || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">计价方式：</span>
              <span class="detail-value">
                <a-tag :color="getPricingMethodColor(customerOrder.pricingMethod)">
                  {{ getPricingMethodText(customerOrder.pricingMethod) }}
                </a-tag>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">金额/单位：</span>
              <span class="detail-value">{{ customerOrderMoneyUnitDisplay }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">支付金额：</span>
              <span class="detail-value">{{ formatMoney(customerOrder.payAmount) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">师傅人数：</span>
              <span class="detail-value">{{ customerOrderWorkerCountDisplay }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">工作开始时间：</span>
              <span class="detail-value">{{ formatDateOnly(customerOrder.workStartTime) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">工作结束时间：</span>
              <span class="detail-value">{{ formatDateOnly(customerOrder.workEndTime) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">每天时段：</span>
              <span class="detail-value">{{ workTimeDisplay }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">创建时间：</span>
              <span class="detail-value">{{ parseTime(customerOrder.createTime) || '-' }}</span>
            </div>
            <div class="detail-row" v-if="customerOrder.picture">
              <span class="detail-label">视频/图片：</span>
              <span class="detail-value"><a @click="showMediaModal = true">查看</a></span>
            </div>
          </div>
          <div class="detail-row detail-row--block content-desc-block">
            <span class="detail-label">内容描述：</span>
            <div v-if="customerOrder.content" class="detail-value order-detail-content" v-html="sanitizeRichText(customerOrder.content)"></div>
            <span v-else class="detail-value">-</span>
          </div>
        </div>

        <div class="order-info-card order-info-card--worker">
          <div class="order-info-card__title">师傅信息</div>
          <a-row type="flex" align="middle" justify="start" :gutter="8" class="worker-row">
            <a-col :span="4" class="worker-col worker-col--avatar">
              <a-avatar
                v-if="masterUser.avatar"
                :src="masterUser.avatar"
                :size="64"
                shape="square"
                class="worker-avatar"
                @click.native="previewImage(masterUser.avatar)"
              />
              <a-avatar v-else :size="64" shape="square" class="worker-avatar worker-avatar--placeholder">
                {{ (masterUser.nickname || form.userName || '-').toString().charAt(0) }}
              </a-avatar>
            </a-col>
            <a-col :span="5" class="worker-col">
              <div class="detail-row"><span class="detail-label">昵称：</span><span class="detail-value">{{ masterUser.nickname || form.userName || '-' }}</span></div>
              <div class="detail-row"><span class="detail-label">手机号：</span><span class="detail-value">{{ masterUser.mobile || '-' }}</span></div>
            </a-col>
            <a-col :span="5" class="worker-col">
              <div class="detail-row"><span class="detail-label">邀请码：</span><span class="detail-value">{{ masterUser.invitationCode || '-' }}</span></div>
              <div class="detail-row"><span class="detail-label">信用分：</span><span class="detail-value">{{ masterUser.creditScore != null ? masterUser.creditScore : '-' }}</span></div>
            </a-col>
            <a-col :span="6" class="worker-col worker-col--credit">
              <div class="detail-row detail-row--align-center">
                <span class="detail-value">
                  <a-progress
                    v-if="masterUser.creditScore != null && masterUser.creditScore !== ''"
                    type="circle"
                    :percent="masterCreditScorePercent"
                    :stroke-color="masterCreditScoreStrokeColor"
                    :width="80"
                    :format="formatCreditScore"
                  />
                  <span v-else>-</span>
                </span>
              </div>
            </a-col>
            <a-col :span="4" class="worker-col worker-col--rate">
              <div class="detail-row detail-row--align-center">
                <span class="detail-label">评价分：</span>
                <span class="detail-value worker-rate-wrap">
                  <span class="worker-rate-stars" aria-hidden="true">
                    <span
                      v-for="i in 5"
                      :key="i"
                      class="worker-rate-star"
                      :class="{ 'is-full': masterCommentScoreRate >= i, 'is-half': masterCommentScoreRate >= i - 0.5 && masterCommentScoreRate < i }"
                    >
                      <span v-if="masterCommentScoreRate >= i" class="star-full">★</span>
                      <span v-else-if="masterCommentScoreRate >= i - 0.5" class="star-half">★</span>
                      <span v-else class="star-empty">☆</span>
                    </span>
                  </span>
                  <span class="detail-value">{{ masterUser.commentScore != null && masterUser.commentScore !== '' ? masterUser.commentScore + '分' : '-' }}</span>
                </span>
              </div>
            </a-col>
          </a-row>
        </div>

        <div class="order-info-card">
          <div class="order-info-card__title order-info-card__title--with-action">
            <span>保险信息</span>
            <a v-if="hasInsurance" @click="openInsuranceDetail">查看详情</a>
          </div>
          <template v-if="hasInsurance">
            <div class="detail-grid">
              <div class="detail-row">
                <span class="detail-label">保险订单ID：</span>
                <span class="detail-value">{{ customerOrder.insuranceInfo.id || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">保险名称：</span>
                <span class="detail-value">{{ customerOrder.insuranceInfo.name || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">支付状态：</span>
                <span class="detail-value">
                  <a-tag :color="getInsuranceBondPayStatusColor(customerOrder.insuranceInfo.bondPayStatus)">
                    {{ getInsuranceBondPayStatusText(customerOrder.insuranceInfo.bondPayStatus) }}
                  </a-tag>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">投保状态：</span>
                <span class="detail-value">
                  <a-tag :color="getUnderwritingStatusColor(customerOrder.insuranceInfo.underwritingStatus)">
                    {{ getUnderwritingStatusText(customerOrder.insuranceInfo.underwritingStatus) }}
                  </a-tag>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">被保人数：</span>
                <span class="detail-value">{{ customerOrder.insuranceInfo.insuredCount != null ? customerOrder.insuranceInfo.insuredCount : '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">第三方单号：</span>
                <span class="detail-value">{{ customerOrder.insuranceInfo.thirdOrderNo || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">支付时间：</span>
                <span class="detail-value">{{ parseTime(customerOrder.insuranceInfo.bondPayDate) || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">最近提交时间：</span>
                <span class="detail-value">{{ parseTime(customerOrder.insuranceInfo.lastSubmitTime) || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">最近成功时间：</span>
                <span class="detail-value">{{ parseTime(customerOrder.insuranceInfo.lastSuccessTime) || '-' }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">最近失败原因：</span>
                <span class="detail-value">{{ customerOrder.insuranceInfo.lastFailReason || '-' }}</span>
              </div>
            </div>

            <div class="detail-row detail-row--block content-desc-block">
              <span class="detail-label">投保人信息：</span>
              <div v-if="insuranceApplicantInfo" class="insurance-person-grid">
                <div class="detail-row"><span class="detail-label">姓名：</span><span class="detail-value">{{ insuranceApplicantInfo.name || '-' }}</span></div>
                <div class="detail-row"><span class="detail-label">手机号：</span><span class="detail-value">{{ insuranceApplicantInfo.mobile || '-' }}</span></div>
                <div class="detail-row"><span class="detail-label">身份证号：</span><span class="detail-value">{{ insuranceApplicantInfo.cardNumber || '-' }}</span></div>
                <div class="detail-row"><span class="detail-label">性别：</span><span class="detail-value">{{ insuranceApplicantInfo.sex || '-' }}</span></div>
                <div class="detail-row"><span class="detail-label">出生日期：</span><span class="detail-value">{{ insuranceApplicantInfo.birthdate || '-' }}</span></div>
                <div class="detail-row"><span class="detail-label">邮箱：</span><span class="detail-value">{{ insuranceApplicantInfo.email || '-' }}</span></div>
              </div>
              <span v-else class="detail-value">-</span>
            </div>

            <div class="detail-row detail-row--block content-desc-block">
              <span class="detail-label">被保人信息：</span>
              <a-table
                v-if="insuranceInsuredList.length > 0"
                class="insurance-table"
                :columns="insuranceColumns"
                :data-source="insuranceInsuredList"
                :pagination="false"
                row-key="tableKey"
                size="small"
              >
                <template slot="sex" slot-scope="sex">
                  {{ getInsuranceSexText(sex) }}
                </template>
                <template slot="birthday" slot-scope="birthday, record">
                  {{ birthday || parseBirthdayFromIdCard(record.idCard) || '-' }}
                </template>
              </a-table>
              <span v-else class="detail-value">-</span>
            </div>
          </template>
          <div v-else class="detail-row detail-row--block">
            <span class="detail-value">暂无投保信息</span>
          </div>
        </div>
      </div>
    </a-spin>

    <order-detail-drawer ref="orderDetailDrawer" />

    <a-modal
      title="视频/图片"
      :visible="showMediaModal"
      :footer="null"
      width="800px"
      @cancel="showMediaModal = false"
    >
      <video
        v-if="showMediaModal && isVideo(customerOrder.picture)"
        :src="customerOrder.picture"
        controls
        style="width: 100%; max-height: 480px; border-radius: 4px; background: #000;"
      />
      <img
        v-else-if="showMediaModal && customerOrder.picture"
        :src="customerOrder.picture"
        style="width: 100%; max-height: 70vh; object-fit: contain; border-radius: 4px; cursor: pointer;"
        @click="previewImage(customerOrder.picture)"
      />
    </a-modal>

    <a-modal
      title="投保详情"
      :visible="insuranceDetailVisible"
      width="900px"
      :footer="null"
      @cancel="closeInsuranceDetail"
    >
      <a-spin :spinning="insuranceDetailLoading">
        <a-descriptions bordered size="small" :column="2">
          <a-descriptions-item label="保险订单ID">{{ insuranceDetailForm.id || '-' }}</a-descriptions-item>
          <a-descriptions-item label="投保状态">
            <a-tag :color="getUnderwritingStatusColor(insuranceDetailForm.underwritingStatus)">
              {{ getUnderwritingStatusText(insuranceDetailForm.underwritingStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="第三方单号">{{ insuranceDetailForm.thirdOrderNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="已重试次数">{{ insuranceDetailForm.retryCount != null ? insuranceDetailForm.retryCount : '-' }}</a-descriptions-item>
          <a-descriptions-item label="最大重试次数">{{ insuranceDetailForm.maxRetry != null ? insuranceDetailForm.maxRetry : '-' }}</a-descriptions-item>
          <a-descriptions-item label="最近一次提交时间">{{ parseTime(insuranceDetailForm.lastSubmitTime) || '-' }}</a-descriptions-item>
          <a-descriptions-item label="最近一次成功时间">{{ parseTime(insuranceDetailForm.lastSuccessTime) || '-' }}</a-descriptions-item>
          <a-descriptions-item label="最近一次失败原因" :span="2">
            <div class="payload-block">{{ insuranceDetailForm.lastFailReason || '-' }}</div>
          </a-descriptions-item>
          <a-descriptions-item label="投保人快照" :span="2">
            <a-textarea :value="insuranceDetailForm.applicantSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 8 }" read-only />
          </a-descriptions-item>
          <a-descriptions-item label="被保人快照" :span="2">
            <a-textarea :value="insuranceDetailForm.insuredSnapshot || ''" :auto-size="{ minRows: 4, maxRows: 10 }" read-only />
          </a-descriptions-item>
          <a-descriptions-item label="第三方请求报文" :span="2">
            <a-textarea :value="insuranceDetailForm.thirdRequestPayload || ''" :auto-size="{ minRows: 5, maxRows: 12 }" read-only />
          </a-descriptions-item>
          <a-descriptions-item label="第三方响应报文" :span="2">
            <a-textarea :value="insuranceDetailForm.thirdResponsePayload || ''" :auto-size="{ minRows: 5, maxRows: 12 }" read-only />
          </a-descriptions-item>
        </a-descriptions>
      </a-spin>
    </a-modal>
  </a-drawer>
</template>

<script>
import { getOrder } from '@/api/master/order'
import { getUser as getBizUser } from '@/api/biz/user'
import { getOrder as getCustomerOrder } from '@/api/order/order'
import { sanitizeRichText } from '@/utils/sanitize'
import { getUnderwritingDetail } from '@/api/insuranceOrder/insuranceOrder'
import OrderDetailDrawer from '@/views/order/order/modules/CreateForm'

export default {
  name: 'CreateForm',
  components: {
    OrderDetailDrawer
  },
  data () {
    return {
      open: false,
      loading: false,
      showMediaModal: false,
      insuranceDetailVisible: false,
      insuranceDetailLoading: false,
      insuranceDetailForm: {},
      formTitle: '师傅订单详情',
      form: this.getDefaultForm(),
      masterUser: this.getDefaultMasterUser(),
      customerOrder: this.getDefaultCustomerOrder(),
      insuranceColumns: [
        { title: '姓名', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
        { title: '身份证号', dataIndex: 'idCard', key: 'idCard', ellipsis: true },
        { title: '性别', dataIndex: 'sex', key: 'sex', width: 90, scopedSlots: { customRender: 'sex' } },
        { title: '出生日期', dataIndex: 'birthday', key: 'birthday', width: 140, scopedSlots: { customRender: 'birthday' } }
      ]
    }
  },
  computed: {
    masterOrderStepCurrent () {
      const status = Number(this.form.masterOrderStatus)
      if (status === -1) {
        return 0
      }
      return status >= 0 && status <= 7 ? status + 1 : 0
    },
    masterOrderStepStatus () {
      return Number(this.form.masterOrderStatus) === 7 ? 'error' : 'process'
    },
    customerOrderMoneyUnitDisplay () {
      if (this.customerOrder.money == null) {
        return '-'
      }
      return `${this.formatMoney(this.customerOrder.money)}${this.customerOrder.unit ? ` / ${this.customerOrder.unit}` : ''}`
    },
    customerOrderWorkerCountDisplay () {
      if (!this.customerOrder.id) {
        return '-'
      }
      const numberOfWorkers = Number(this.customerOrder.numberOfWorkers || 0)
      const man = Number(this.customerOrder.man || 0)
      const woman = Number(this.customerOrder.woman || 0)
      return `${numberOfWorkers + man + woman}人`
    },
    masterCreditScorePercent () {
      const score = Number(this.masterUser.creditScore || 0)
      return Math.max(0, Math.min(100, score))
    },
    masterCreditScoreStrokeColor () {
      const score = Number(this.masterUser.creditScore || 0)
      if (score >= 90) {
        return '#52c41a'
      }
      if (score >= 75) {
        return '#1890ff'
      }
      if (score >= 60) {
        return '#faad14'
      }
      return '#ff4d4f'
    },
    masterCommentScoreRate () {
      const score = Number(this.masterUser.commentScore || 0)
      return Math.max(0, Math.min(5, score / 20))
    },
    hasInsurance () {
      return !!(this.customerOrder.insuranceInfo && this.customerOrder.insuranceInfo.id)
    },
    insuranceApplicantInfo () {
      return this.parseJsonObject(this.customerOrder.insuranceInfo && this.customerOrder.insuranceInfo.applicantSnapshot)
    },
    insuranceInsuredList () {
      const list = this.parseJsonArray(this.customerOrder.insuranceInfo && this.customerOrder.insuranceInfo.insuredSnapshot)
      return list.map((item, index) => ({
        ...item,
        tableKey: `${item.id || index}-${item.idCard || ''}`
      }))
    },
    workTimeDisplay () {
      return this.formatWorkTime(this.customerOrder.workTime)
    }
  },
  methods: {
    sanitizeRichText,
    getDefaultForm () {
      return {
        id: null,
        userId: null,
        userName: null,
        customerOrderId: null,
        masterOrderStatus: null,
        aftermarketStatus: null,
        aftermarketNum: null,
        orderUnitPrice: null,
        orderTotalPrice: null,
        quotationPrice: null,
        quotationTime: null,
        bondPrice: null,
        bondPayType: null,
        bondPayStatus: null,
        startCode: null,
        createTime: null,
        remark: null
      }
    },
    getDefaultMasterUser () {
      return {
        id: null,
        avatar: null,
        nickname: null,
        mobile: null,
        creditScore: null,
        commentScore: null,
        invitationCode: null
      }
    },
    getDefaultCustomerOrder () {
      return {
        id: null,
        title: null,
        type: null,
        status: null,
        category: null,
        categoryName: null,
        workLocation: null,
        address: null,
        pricingMethod: null,
        money: null,
        unit: null,
        payAmount: null,
        numberOfWorkers: null,
        man: null,
        woman: null,
        workStartTime: null,
        workEndTime: null,
        workTime: null,
        createTime: null,
        picture: null,
        content: null,
        insuranceInfo: null
      }
    },
    onClose () {
      this.open = false
      this.reset()
    },
    cancel () {
      this.onClose()
    },
    reset () {
      this.form = this.getDefaultForm()
      this.masterUser = this.getDefaultMasterUser()
      this.customerOrder = this.getDefaultCustomerOrder()
      this.showMediaModal = false
      this.closeInsuranceDetail()
    },
    seedCustomerOrderFromRow (row) {
      if (!row) {
        return
      }
      this.customerOrder = {
        ...this.getDefaultCustomerOrder(),
        id: row.customerOrderId || null,
        title: row.customerOrderTitle || null,
        type: row.customerOrderType,
        status: row.customerOrderStatus,
        pricingMethod: row.customerOrderPricingMethod,
        money: row.customerOrderMoney,
        payAmount: row.customerOrderPayAmount,
        numberOfWorkers: row.customerOrderNumberOfWorkers,
        man: row.customerOrderMan,
        woman: row.customerOrderWoman,
        createTime: row.customerOrderCreateTime
      }
    },
    handleAdd () {
      this.$message.info('师傅订单仅支持查看详情')
    },
    handleUpdate (row, ids) {
      this.reset()
      const id = row ? row.id : (Array.isArray(ids) ? ids[0] : ids)
      if (id == null) {
        this.$message.warning('请选择一条记录')
        return
      }
      this.form = {
        ...this.getDefaultForm(),
        ...(row || {}),
        id
      }
      this.seedCustomerOrderFromRow(row)
      this.open = true
      this.formTitle = '师傅订单详情'
      this.loading = true
      getOrder({ id }).then(response => {
        this.form = {
          ...this.getDefaultForm(),
          ...(row || {}),
          ...(response.data || {})
        }
        this.loadMasterUserInfo(this.form.userId)
        this.loadCustomerOrderInfo(this.form.customerOrderId)
      }).finally(() => {
        this.loading = false
      })
    },
    loadMasterUserInfo (userId) {
      if (!userId) {
        return
      }
      getBizUser({ id: userId }).then(response => {
        this.masterUser = {
          ...this.getDefaultMasterUser(),
          ...(response.data || {})
        }
      }).catch(() => {})
    },
    loadCustomerOrderInfo (id) {
      if (!id) {
        return
      }
      getCustomerOrder({ id }).then(response => {
        this.customerOrder = {
          ...this.getDefaultCustomerOrder(),
          ...(response.data || {})
        }
      }).catch(() => {})
    },
    openCustomerOrderDetail () {
      if (!this.customerOrder.id) {
        this.$message.warning('暂无关联雇主订单')
        return
      }
      this.$refs.orderDetailDrawer.handleUpdate({ id: this.customerOrder.id }, undefined)
    },
    formatMoney (value) {
      return value != null ? `¥${Number(value).toFixed(2)}` : '-'
    },
    formatDateOnly (value) {
      if (!value) {
        return '-'
      }
      return this.parseTime(value, '{y}-{m}-{d}') || '-'
    },
    formatCreditScore (percent) {
      return percent != null ? `${Math.round(percent)}分` : '-'
    },
    formatWorkTime (value) {
      if (!value) {
        return '-'
      }
      try {
        const list = typeof value === 'string' ? JSON.parse(value) : value
        if (!Array.isArray(list) || list.length === 0) {
          return typeof value === 'string' ? value : '-'
        }
        return list.map(item => `${item.startTime || ''}-${item.endTime || ''}`).filter(Boolean).join('，') || '-'
      } catch (e) {
        return String(value)
      }
    },
    parseJsonObject (value) {
      if (!value || typeof value !== 'string') {
        return null
      }
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
      } catch (e) {
        return null
      }
    },
    parseJsonArray (value) {
      if (!value || typeof value !== 'string') {
        return []
      }
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    getInsuranceSexText (sex) {
      if (sex === '男' || sex === '女') {
        return sex
      }
      if (Number(sex) === 2) {
        return '女'
      }
      if (sex != null && sex !== '') {
        return '男'
      }
      return '-'
    },
    parseBirthdayFromIdCard (idCard) {
      if (!idCard || typeof idCard !== 'string') {
        return ''
      }
      const normalized = idCard.trim()
      if (normalized.length < 14) {
        return ''
      }
      const birthday = normalized.slice(6, 14)
      if (!/^\d{8}$/.test(birthday)) {
        return ''
      }
      return `${birthday.slice(0, 4)}-${birthday.slice(4, 6)}-${birthday.slice(6, 8)}`
    },
    getInsuranceBondPayStatusText (status) {
      const map = {
        1: '待支付',
        2: '已支付',
        3: '已取消'
      }
      return map[status] || '-'
    },
    getInsuranceBondPayStatusColor (status) {
      const map = {
        1: 'orange',
        2: 'green',
        3: 'red'
      }
      return map[status] || 'default'
    },
    getUnderwritingStatusText (status) {
      const map = {
        0: '未触发投保',
        1000: '待投保',
        1100: '投保中',
        1200: '投保成功',
        1250: '部分成功',
        1300: '投保失败待重试',
        1400: '投保失败',
        1500: '已取消'
      }
      return map[status] || '-'
    },
    getUnderwritingStatusColor (status) {
      const map = {
        0: 'blue',
        1000: 'cyan',
        1100: 'processing',
        1200: 'green',
        1250: 'gold',
        1300: 'orange',
        1400: 'red',
        1500: 'purple'
      }
      return map[status] || 'default'
    },
    getMasterOrderStatusText (status) {
      const map = {
        '-1': '待支付',
        0: '接单中',
        1: '待服务',
        2: '服务中',
        3: '待评价',
        4: '已完成',
        5: '售后中',
        6: '售后完成',
        7: '已关闭'
      }
      return map[status] || '-'
    },
    getMasterOrderStatusColor (status) {
      const map = {
        '-1': 'gold',
        0: 'blue',
        1: 'orange',
        2: 'cyan',
        3: 'purple',
        4: 'green',
        5: 'volcano',
        6: 'lime',
        7: 'geekblue'
      }
      return map[status] || 'default'
    },
    getAftermarketStatusText (status) {
      const map = {
        0: '无需售后',
        1: '待联系客户',
        2: '待售后完成',
        3: '已提交售后',
        4: '售后完成',
        5: '平台审核'
      }
      return map[status] || '-'
    },
    getAftermarketStatusColor (status) {
      const map = {
        0: 'blue',
        1: 'orange',
        2: 'gold',
        3: 'blue',
        4: 'green',
        5: 'purple'
      }
      return map[status] || 'default'
    },
    getBondPayTypeText (status) {
      const map = {
        1: '微信',
        2: '支付宝'
      }
      return map[status] || '-'
    },
    getBondPayTypeColor (status) {
      const map = {
        1: 'green',
        2: 'blue'
      }
      return map[status] || 'default'
    },
    getBondPayStatusText (status) {
      const map = {
        0: '不需支付',
        1: '待支付',
        2: '已支付',
        3: '已取消'
      }
      return map[status] || '-'
    },
    getBondPayStatusColor (status) {
      const map = {
        0: 'blue',
        1: 'orange',
        2: 'green',
        3: 'red'
      }
      return map[status] || 'default'
    },
    getOrderStatusText (status) {
      const map = {
        '-2': '未支付',
        '-1': '未实名认证',
        0: '接单中',
        1: '服务中',
        2: '已完成',
        3: '售后中',
        4: '已取消'
      }
      return map[status] || '-'
    },
    getOrderStatusColor (status) {
      const map = {
        '-2': 'orange',
        '-1': 'gold',
        0: 'blue',
        1: 'cyan',
        2: 'green',
        3: 'purple',
        4: 'red'
      }
      return map[status] || 'default'
    },
    getPricingMethodText (status) {
      const map = {
        0: '议价',
        1: '按时计价',
        2: '按天计价',
        3: '按量计价',
        4: '一口价'
      }
      return map[status] || '-'
    },
    getPricingMethodColor (status) {
      const map = {
        0: 'geekblue',
        1: 'blue',
        2: 'green',
        3: 'purple',
        4: 'orange'
      }
      return map[status] || 'default'
    },
    previewImage (url) {
      if (!url) {
        return
      }
      window.open(url, '_blank')
    },
    isVideo (url) {
      if (!url) {
        return false
      }
      return /\.(mp4|webm|ogg|mov)$/i.test(url)
    },
    openInsuranceDetail () {
      if (!this.hasInsurance) {
        return
      }
      this.insuranceDetailVisible = true
      this.insuranceDetailLoading = true
      getUnderwritingDetail({ id: this.customerOrder.insuranceInfo.id }).then(response => {
        if (response && response.success) {
          this.insuranceDetailForm = response.data || {}
        } else {
          this.insuranceDetailForm = {}
        }
      }).finally(() => {
        this.insuranceDetailLoading = false
      })
    },
    closeInsuranceDetail () {
      this.insuranceDetailVisible = false
      this.insuranceDetailLoading = false
      this.insuranceDetailForm = {}
    }
  }
}
</script>

<style scoped>
.master-order-detail-wrap {
  padding-bottom: 8px;
}

.order-status-step {
  margin: 0 0 20px;
  padding: 16px 20px 8px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.order-info-card {
  margin-bottom: 16px;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.order-info-card__title {
  margin-bottom: 16px;
  padding-left: 10px;
  border-left: 4px solid #1890ff;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.order-info-card__title--with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px 24px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  min-height: 32px;
  line-height: 22px;
}

.detail-row--block {
  display: block;
  width: 100%;
}

.detail-row--align-center {
  justify-content: center;
}

.detail-label {
  flex-shrink: 0;
  color: #8c8c8c;
}

.detail-value {
  color: #262626;
  word-break: break-word;
}

.detail-value--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-desc-block {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.order-detail-content {
  font-size: 12px;
  color: #595959;
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
}

.order-detail-content /deep/ img {
  max-width: 100%;
  height: auto;
}

.worker-row {
  min-height: 100px;
}

.worker-col {
  min-height: 84px;
}

.worker-col--avatar,
.worker-col--credit,
.worker-col--rate {
  display: flex;
  align-items: center;
  justify-content: center;
}

.worker-avatar {
  cursor: pointer;
  background: #f0f5ff;
}

.worker-avatar--placeholder {
  color: #1890ff;
  font-size: 24px;
  font-weight: 600;
}

.worker-rate-wrap {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
}

.worker-rate-stars {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  margin-right: 4px;
  vertical-align: middle;
}

.worker-rate-star {
  display: inline-block;
  margin-right: 2px;
}

.worker-rate-stars .star-full,
.worker-rate-stars .star-half,
.worker-rate-stars .star-empty {
  color: #fadb14;
}

.worker-rate-stars .star-empty {
  color: #e8e8e8;
}

.worker-rate-stars .star-half {
  display: inline-block;
  width: 0.5em;
  overflow: hidden;
}

.insurance-person-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 24px;
  margin-top: 8px;
}

.insurance-table {
  margin-top: 8px;
}

.payload-block {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
